// Auth against the real /v1/auth/* endpoints (ADR-0009). Email+password
// only -- that's all the backend supports today (OAuth deferred per
// ADR-0009 Decision 2).
//
// TOKEN STORAGE TRADEOFF (documented per task requirement):
// The JWT is stored in localStorage, not an httpOnly cookie. This is a
// deliberate choice, not an oversight:
//   - The backend issues the token as a JSON body field
//     (`{"access_token": "...", "token_type": "bearer"}`) and expects it
//     back as an `Authorization: Bearer` header (auth.py's docstring is
//     explicit: "expected to be bearer-token based, not cookie-based, so
//     credentialed CORS is not anticipated to ever be needed here"). It
//     never sets a cookie itself. An httpOnly cookie would require the API
//     to start setting one on login/register and this dashboard to switch
//     every authenticated call to `credentials: "include"` plus the API to
//     flip `allow_credentials` to `true` for this dashboard's origin --
//     all backend changes, out of scope for this client-only build
//     (explicitly listed as "do not touch backend code").
//   - Given that constraint, localStorage is the only storage that works
//     with the existing contract. The real risk it introduces is XSS-based
//     token theft (a cookie without `httpOnly` is no safer than
//     localStorage against that; the risk is specifically that an
//     httpOnly cookie can't be read by *any* JS, malicious or not).
//     Mitigations actually applied in this codebase: a strict CSP
//     (script-src 'self', no inline scripts anywhere), zero use of
//     `innerHTML`/`insertAdjacentHTML` on API-derived data anywhere in this
//     app (see util/dom.ts) -- the exact XSS pattern
//     ops/dashboard-client-audit.md found in the legacy dashboard is
//     structurally impossible to reintroduce here since there is no
//     innerHTML call in the codebase at all.
//   - No CSRF exposure either way: this is a bearer-token header, not an
//     ambiently-sent cookie, so there's no request-forging surface
//     regardless of storage location.
//   - Follow-up (out of scope here): if/when the backend adds cookie-based
//     sessions, switching this dashboard to httpOnly cookies is the
//     stronger long-term choice and should happen then.

import { apiFetch, ApiError, _setTokenGetter } from "./client";
import type { DeviceRecord, DevicesResponse, LoginRequest, RegisterRequest, TokenResponse } from "./types";

const STORAGE_KEY = "btclock_access_token";

// B-005 (cross-subdomain auth handoff): in ADDITION to localStorage, the
// JWT is mirrored into a cookie scoped to the parent domain `.bitclock.net`
// so the marketing site (bitclock.net) can see that the user is logged in on
// the dashboard (app.bitclock.net) and start Stripe checkout with the same
// token. localStorage is per-origin and cannot cross that boundary; a
// `Domain=.bitclock.net` cookie is the shared surface both subdomains read.
//
// The cookie is deliberately NOT HttpOnly: this token is a bearer credential
// that client-side JS must be able to read to send as an `Authorization:
// Bearer` header (both here and on the marketing page). It is therefore no
// more exposed to XSS than the existing localStorage copy already is -- the
// same strict CSP (script-src 'self', zero innerHTML) is the real mitigation
// on both surfaces. `Secure` (HTTPS-only) + `SameSite=Lax` are set; there is
// no CSRF surface because the API only ever accepts this token as an explicit
// header, never as an ambiently-sent cookie (the API does not read cookies).
//
// On a non-bitclock.net host (e.g. localhost dev over http) the browser
// rejects a `Domain=.bitclock.net; Secure` cookie outright -- that is a
// harmless no-op, localStorage still carries the session in dev.
const COOKIE_NAME = "bitclock_jwt";
const COOKIE_DOMAIN = ".bitclock.net";
const DEFAULT_MAX_AGE_SECONDS = 30 * 24 * 60 * 60; // matches API jwt_expiry_hours (30d)

/** Seconds until the JWT's own `exp` claim, so the cookie's Max-Age tracks
 * the real token lifetime instead of a guessed constant. Falls back to the
 * 30-day default if the token can't be decoded. */
function tokenMaxAgeSeconds(token: string): number {
  try {
    const payload = token.split(".")[1];
    if (!payload) return DEFAULT_MAX_AGE_SECONDS;
    const b64 = payload.replace(/-/g, "+").replace(/_/g, "/");
    const claims = JSON.parse(atob(b64)) as { exp?: unknown };
    if (typeof claims.exp === "number") {
      const secs = claims.exp - Math.floor(Date.now() / 1000);
      return secs > 0 ? secs : 0;
    }
  } catch {
    // Malformed/unexpected token shape -- fall through to the default.
  }
  return DEFAULT_MAX_AGE_SECONDS;
}

function setSharedCookie(token: string): void {
  try {
    const maxAge = tokenMaxAgeSeconds(token);
    document.cookie =
      `${COOKIE_NAME}=${token}; Domain=${COOKIE_DOMAIN}; Path=/; ` +
      `Secure; SameSite=Lax; Max-Age=${maxAge}`;
  } catch {
    // document.cookie can throw in locked-down contexts -- treat as best-
    // effort, localStorage remains the authoritative session on this origin.
  }
}

function clearSharedCookie(): void {
  try {
    document.cookie =
      `${COOKIE_NAME}=; Domain=${COOKIE_DOMAIN}; Path=/; ` +
      `Secure; SameSite=Lax; Max-Age=0`;
  } catch {
    /* no-op */
  }
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    // localStorage can throw in some contexts (e.g. privacy mode) -- treat
    // as "no session" rather than crash the app.
    return null;
  }
}

function storeToken(token: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, token);
  } catch {
    // If storage fails the user will just be logged out on reload; not a
    // fatal error for the current session.
  }
  // Mirror into the cross-subdomain cookie (B-005) so bitclock.net can see
  // this session. Best-effort and independent of the localStorage write.
  setSharedCookie(token);
}

function clearToken(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
  // Always clear the shared cookie too, on every logout/delete/401 path.
  clearSharedCookie();
}

_setTokenGetter(getStoredToken);

export function isLoggedIn(): boolean {
  return getStoredToken() !== null;
}

// --- SW-3 device cap (3-device login limit, client side) ---
// A stable per-browser UUID identifies this "device" to the API. Minted
// once, kept in localStorage; if storage is unavailable the id is still
// stable for the session via the module-level cache (a fresh id per
// session just counts as a new device -- the server cap handles it).
const DEVICE_ID_KEY = "btclock_device_id";
let deviceIdCache: string | null = null;

function getDeviceId(): string {
  if (deviceIdCache) return deviceIdCache;
  try {
    deviceIdCache = localStorage.getItem(DEVICE_ID_KEY);
  } catch {
    /* storage unavailable -- fall through to minting */
  }
  if (!deviceIdCache) {
    deviceIdCache = crypto.randomUUID();
    try {
      localStorage.setItem(DEVICE_ID_KEY, deviceIdCache);
    } catch {
      /* best-effort persistence only */
    }
  }
  return deviceIdCache;
}

/** Simple, friendly device name for the API's device list. Order matters:
 * Edge UAs contain "Chrome/", Chrome UAs contain "Safari/". */
function deviceName(): string {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : "";
  const browser = ua.includes("Edg/")
    ? "Edge"
    : ua.includes("Firefox/")
      ? "Firefox"
      : ua.includes("Chrome/")
        ? "Chrome"
        : ua.includes("Safari/")
          ? "Safari"
          : "Browser";
  return `Web — ${browser}`;
}

export interface DeviceLimitInfo {
  message: string;
  upsell: { offer: string; prompt: string } | null;
  /** SW-22: the devices holding the slots (name + last seen), when the API
   * sends them; empty on the older 409 shape. */
  devices: { device_name: string | null; last_seen_utc: string | null }[];
}

/** Recognize the device-cap 409 (FIXED contract:
 * {"error":"device_limit","message":...,"upsell":{"offer":...,"prompt":...},
 *  "devices":[{"device_name","last_seen_utc"}]})
 * so the login form can show the message + upsell capture. Returns null for
 * every other error. */
export function deviceLimitInfo(err: unknown): DeviceLimitInfo | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as
    | {
        error?: unknown;
        message?: unknown;
        upsell?: { offer?: unknown; prompt?: unknown } | null;
        devices?: unknown;
      }
    | null;
  if (!body || typeof body !== "object" || body.error !== "device_limit") return null;
  const upsell =
    body.upsell && typeof body.upsell.prompt === "string"
      ? { offer: String(body.upsell.offer ?? ""), prompt: body.upsell.prompt }
      : null;
  const devices = Array.isArray(body.devices)
    ? body.devices
        .filter((d): d is Record<string, unknown> => !!d && typeof d === "object")
        .map((d) => ({
          device_name: typeof d.device_name === "string" ? d.device_name : null,
          last_seen_utc: typeof d.last_seen_utc === "string" ? d.last_seen_utc : null,
        }))
    : [];
  return {
    message: typeof body.message === "string" ? body.message : "Device limit reached.",
    upsell,
    devices,
  };
}

// --- SW-22: the account screen's Devices section (FIXED contract). ---
export function listDevices(): Promise<DevicesResponse> {
  return apiFetch<DevicesResponse>("/v1/devices", { auth: true });
}

/** DELETE /v1/devices/{device_id} -> 204 (404 if not this user's). */
export function revokeDevice(deviceId: string): Promise<void> {
  return apiFetch<void>(`/v1/devices/${encodeURIComponent(deviceId)}`, { method: "DELETE", auth: true });
}

/** DELETE /v1/devices/others -> {"revoked": n}. */
export function revokeOtherDevices(): Promise<{ revoked: number }> {
  return apiFetch<{ revoked: number }>("/v1/devices/others", { method: "DELETE", auth: true });
}

/** "This device" = the API's `current` flag (the token's device claim), or
 * the id this browser sends at login (getDeviceId) -- the same value. */
export function isThisDevice(device: DeviceRecord): boolean {
  return device.current || device.device_id === getDeviceId();
}

/** Record the user's answer to the 5-device upsell prompt. Unauthenticated
 * by contract (the user just FAILED to log in). */
export function submitUpsellInterest(
  email: string,
  response: "interested" | "not_interested"
): Promise<void> {
  return apiFetch<void>("/v1/devices/upsell-interest", {
    method: "POST",
    body: { email, response },
  });
}

export async function register(body: RegisterRequest): Promise<void> {
  const res = await apiFetch<TokenResponse>("/v1/auth/register", {
    method: "POST",
    body,
  });
  storeToken(res.access_token);
}

export async function login(body: LoginRequest): Promise<void> {
  const res = await apiFetch<TokenResponse>("/v1/auth/login", {
    method: "POST",
    // SW-3: identify this browser to the API's 3-device cap.
    body: { ...body, device_id: getDeviceId(), device_name: deviceName() },
  });
  storeToken(res.access_token);
}

export function logout(): void {
  // SW-3: release this device's slot server-side. Fire-and-forget -- the
  // local sign-out must never be blocked by a failed/slow API call. The
  // bearer header is captured before clearToken() runs (apiFetch reads the
  // token synchronously before its first await).
  if (isLoggedIn()) {
    void apiFetch<void>("/v1/devices/current", { method: "DELETE", auth: true }).catch(() => {});
  }
  clearToken();
}

/** Start a password reset (T-098). Unauthenticated -- the whole point is
 * that the user can't log in. The backend ALWAYS returns 200 with a
 * generic message whether or not the email is registered (anti-
 * enumeration), so this resolves on success and the caller shows the same
 * "check your email" message regardless. No token is stored; the reset
 * link is delivered by email and completes on the marketing site's
 * /reset page. */
export async function requestPasswordReset(email: string): Promise<void> {
  await apiFetch<{ message: string }>("/v1/auth/request-password-reset", {
    method: "POST",
    body: { email },
  });
}

export async function deleteAccount(): Promise<void> {
  await apiFetch<void>("/v1/auth/account", { method: "DELETE", auth: true });
  clearToken();
}

export type EntitlementStatus = "loggedOut" | "free" | "subscribed" | "unknown";

/** There is no dedicated GET /v1/subscription-status endpoint on the API
 * (verified against the current route list -- only alerts/auth/billing/
 * current/daily-extremes/24h-stats/history/stats/stream/health/push exist).
 * Adding one would be a backend change, out of scope here. Instead, this
 * probes the cheapest real history-class endpoint
 * (`require_entitlement`-gated per ADR-0008) with a minimal 1-minute
 * window and reads the real status code the backend already returns to
 * distinguish "not logged in" (401) from "logged in, no active
 * subscription" (402) from "entitled" (200) -- no new backend surface,
 * just an honest read of an existing, real gate. */
export async function checkEntitlement(): Promise<EntitlementStatus> {
  if (!isLoggedIn()) return "loggedOut";
  try {
    await apiFetch<unknown>("/v1/history?minutes=1", { auth: true });
    return "subscribed";
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.status === 401) {
        clearToken(); // token expired/invalid server-side -- drop it locally too
        return "loggedOut";
      }
      if (err.status === 402) return "free";
    }
    return "unknown";
  }
}
